import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { useLang } from "../../contexts/LangContext";
import { t } from "../../i18n/translations";
import { Check, Trash2, Gift, Plus, Download, Mail, Package } from "lucide-react";
import { toast } from "sonner";

export default function AdminVouchers() {
  const { lang } = useLang();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => { const { data } = await api.get("/admin/vouchers"); setItems(data || []); };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    await api.put(`/admin/vouchers/${id}/status`, { status });
    toast.success("✓");
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Löschen?")) return;
    await api.delete(`/admin/vouchers/${id}`);
    load();
  };

  const downloadPdf = async (v) => {
    try {
      const token = localStorage.getItem("admin_token");
      const backend = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${backend}/api/admin/vouchers/${v.id}/pdf`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("pdf failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Gutschein-${v.voucher_code || v.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("PDF-Download fehlgeschlagen");
    }
  };

  const sendEmail = async (v) => {
    if (!v.buyer_email) { toast.error("Käuferin hat keine E-Mail hinterlegt"); return; }
    if (!window.confirm(`Gutschein per E-Mail an ${v.buyer_email} senden?`)) return;
    try {
      await api.post(`/admin/vouchers/${v.id}/send-email`);
      toast.success("E-Mail versendet");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "E-Mail-Versand fehlgeschlagen");
    }
  };

  const filtered = items.filter((v) => filter === "all" || v.status === filter);

  const STATUS_LABEL = {
    pending: "Offen",
    paid: "Bezahlt",
    sent: "Versendet",
    picked_up: "Abgeholt",
  };
  const statusCls = (s) =>
    s === "sent" ? "bg-sage/15 text-sage"
    : s === "picked_up" ? "bg-sage/25 text-sage"
    : s === "paid" ? "bg-terracotta/10 text-terracotta"
    : "bg-ink/10 text-ink-muted";

  return (
    <div className="p-5 sm:p-8 lg:p-10" data-testid="admin-vouchers-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-3xl sm:text-4xl text-ink flex items-center gap-3"><Gift className="w-6 h-6 sm:w-7 sm:h-7 text-terracotta" strokeWidth={1.5} /> Gutscheine</h1>
        <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto justify-between sm:justify-end">
          <div className="flex gap-2 text-xs overflow-x-auto pb-1">
            {["all", "pending", "paid", "sent", "picked_up"].map((f) => (
              <button key={f} onClick={() => setFilter(f)}
                className={`shrink-0 px-3 py-1.5 rounded-full uppercase tracking-wider ${filter === f ? "bg-ink text-white" : "bg-white border border-border text-ink-muted"}`}
                data-testid={`voucher-filter-${f}`}>
                {f === "all" ? t(lang, "admin.all") : STATUS_LABEL[f]}
              </button>
            ))}
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-terracotta text-white rounded-full text-sm hover:bg-terracotta-hover"
            data-testid="voucher-create-button"
          >
            <Plus className="w-4 h-4" strokeWidth={2} /> Gutschein erstellen
          </button>
        </div>
      </div>

      {/* Mobile cards */}
      <div className="mt-6 space-y-3 md:hidden">
        {filtered.map((v) => (
          <div key={v.id} className="bg-white border border-border rounded-md p-4" data-testid={`voucher-card-${v.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="font-display text-3xl text-ink">CHF {v.amount_chf}</div>
              <span className={`text-[10px] px-2 py-1 rounded-full ${statusCls(v.status)}`}>{STATUS_LABEL[v.status] || v.status}</span>
            </div>
            <div className="mt-3 pt-3 border-t border-border text-sm">
              <div className="text-ink">{v.buyer_name}</div>
              {v.buyer_email && <a href={`mailto:${v.buyer_email}`} className="text-xs text-terracotta break-all">{v.buyer_email}</a>}
              {v.buyer_phone && <a href={`tel:${v.buyer_phone}`} className="block text-xs text-ink-muted">{v.buyer_phone}</a>}
              {v.recipient_name && <div className="text-xs text-ink-muted mt-2">Empfängerin: <span className="text-ink">{v.recipient_name}</span></div>}
              {v.message && <div className="text-xs text-ink-muted mt-1 italic">„{v.message}"</div>}
              {v.voucher_code && <div className="text-[10px] uppercase tracking-widest text-ink-muted mt-2 font-mono">{v.voucher_code}</div>}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button onClick={() => downloadPdf(v)} className="px-3 py-2 text-xs bg-muted text-ink rounded-md inline-flex items-center gap-1"><Download className="w-3.5 h-3.5" strokeWidth={1.5}/> PDF</button>
              {v.buyer_email && <button onClick={() => sendEmail(v)} className="px-3 py-2 text-xs bg-terracotta/10 text-terracotta rounded-md inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" strokeWidth={1.5}/> E-Mail</button>}
              {v.status !== "picked_up" && <button onClick={() => updateStatus(v.id, "picked_up")} className="px-3 py-2 text-xs bg-sage/15 text-sage rounded-md inline-flex items-center gap-1"><Package className="w-3.5 h-3.5" strokeWidth={1.5}/> Abgeholt</button>}
              {v.status !== "paid" && <button onClick={() => updateStatus(v.id, "paid")} className="px-3 py-2 text-xs bg-terracotta/10 text-terracotta rounded-md">Bezahlt</button>}
              {v.status !== "sent" && <button onClick={() => updateStatus(v.id, "sent")} className="px-3 py-2 text-xs bg-sage/15 text-sage rounded-md inline-flex items-center gap-1"><Check className="w-3.5 h-3.5" strokeWidth={1.5}/> Versendet</button>}
              <button onClick={() => del(v.id)} className="px-3 py-2 text-xs bg-muted text-ink-muted rounded-md"><Trash2 className="w-3.5 h-3.5" strokeWidth={1.5}/></button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="text-center py-10 text-ink-muted">—</div>}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block mt-8 bg-white border border-border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/60 text-xs uppercase tracking-wider text-ink-muted">
            <tr>
              <th className="text-left px-5 py-3">Betrag</th>
              <th className="text-left px-5 py-3">Käuferin</th>
              <th className="text-left px-5 py-3">Empfängerin</th>
              <th className="text-left px-5 py-3">Nachricht</th>
              <th className="text-left px-5 py-3">Status</th>
              <th className="text-left px-5 py-3">Erstellt</th>
              <th className="text-right px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((v) => (
              <tr key={v.id} className="border-t border-border" data-testid={`voucher-row-${v.id}`}>
                <td className="px-5 py-4 font-display text-2xl text-ink align-top">
                  CHF {v.amount_chf}
                  {v.voucher_code && <div className="text-[10px] uppercase tracking-widest text-ink-muted font-sans mt-1 font-mono">{v.voucher_code}</div>}
                  <div className="text-[10px] uppercase tracking-wider text-ink-muted font-sans mt-1">
                    {v.voucher_type === "service" ? (v.service_name || "Behandlung") : "Geldbetrag"} · {v.delivery_method === "shipping" ? "Post" : v.delivery_method === "email" ? "E-Mail" : "Abholung"}
                    {v.source === "admin_manual" && <span className="ml-1">· manuell</span>}
                  </div>
                </td>
                <td className="px-5 py-4 align-top">
                  <div className="text-ink">{v.buyer_name || "—"}</div>
                  {v.buyer_email && <a href={`mailto:${v.buyer_email}`} className="text-xs text-ink-muted hover:text-terracotta">{v.buyer_email}</a>}
                  {v.buyer_phone && <div className="text-xs text-ink-muted">{v.buyer_phone}</div>}
                </td>
                <td className="px-5 py-4 text-ink align-top">{v.recipient_name || "—"}</td>
                <td className="px-5 py-4 text-ink-muted text-xs italic align-top max-w-xs">{v.message || "—"}</td>
                <td className="px-5 py-4 align-top"><span className={`text-xs px-2.5 py-1 rounded-full ${statusCls(v.status)}`}>{STATUS_LABEL[v.status] || v.status}</span></td>
                <td className="px-5 py-4 text-xs text-ink-muted align-top">{(v.created_at || "").slice(0, 10)}</td>
                <td className="px-5 py-4 align-top text-right">
                  <div className="inline-flex items-center gap-1 flex-wrap justify-end">
                    <button onClick={() => downloadPdf(v)} className="px-2 py-1.5 text-xs rounded hover:bg-muted text-ink-muted inline-flex items-center gap-1" data-testid={`v-pdf-${v.id}`} title="PDF herunterladen zum Drucken">
                      <Download className="w-3.5 h-3.5" strokeWidth={1.5}/> PDF
                    </button>
                    {v.buyer_email && (
                      <button onClick={() => sendEmail(v)} className="px-2 py-1.5 text-xs rounded hover:bg-terracotta/10 text-terracotta inline-flex items-center gap-1" data-testid={`v-email-${v.id}`} title="Per E-Mail versenden">
                        <Mail className="w-3.5 h-3.5" strokeWidth={1.5}/> E-Mail
                      </button>
                    )}
                    {v.status !== "picked_up" && (
                      <button onClick={() => updateStatus(v.id, "picked_up")} className="px-2 py-1.5 text-xs rounded hover:bg-sage/15 text-sage inline-flex items-center gap-1" data-testid={`v-picked-${v.id}`} title="Als abgeholt markieren">
                        <Package className="w-3.5 h-3.5" strokeWidth={1.5}/> Abgeholt
                      </button>
                    )}
                    {v.status !== "paid" && <button onClick={() => updateStatus(v.id, "paid")} className="px-2 py-1.5 text-xs rounded hover:bg-terracotta/10 text-terracotta" data-testid={`v-paid-${v.id}`}>Bezahlt</button>}
                    {v.status !== "sent" && <button onClick={() => updateStatus(v.id, "sent")} className="px-2 py-1.5 text-xs rounded hover:bg-sage/15 text-sage inline-flex items-center gap-1" data-testid={`v-sent-${v.id}`}><Check className="w-3.5 h-3.5" strokeWidth={1.5} /> Versendet</button>}
                    <button onClick={() => del(v.id)} className="p-2 rounded hover:bg-muted text-ink-muted" data-testid={`v-del-${v.id}`}><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={7} className="px-5 py-10 text-center text-ink-muted">—</td></tr>}
          </tbody>
        </table>
      </div>

      {showCreate && <CreateVoucherModal onClose={() => setShowCreate(false)} onCreated={() => { load(); setShowCreate(false); }} />}
    </div>
  );
}

function CreateVoucherModal({ onClose, onCreated }) {
  const [saving, setSaving] = useState(false);
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({
    voucher_type: "amount",
    amount_chf: 100,
    service_id: "",
    buyer_name: "",
    buyer_email: "",
    buyer_phone: "",
    recipient_name: "",
    message: "",
    delivery_method: "pickup",
    initial_status: "paid",
  });

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/services");
        setServices(r.data || []);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const selectedService = services.find((s) => s.id === form.service_id);
  const effectiveAmount = form.voucher_type === "service" && selectedService ? selectedService.price_chf : form.amount_chf;

  const submit = async (e) => {
    e.preventDefault();
    if (form.voucher_type === "service" && !form.service_id) { toast.error("Bitte eine Behandlung wählen"); return; }
    if (form.voucher_type === "amount" && (!form.amount_chf || Number(form.amount_chf) <= 0)) { toast.error("Bitte Betrag eingeben"); return; }
    setSaving(true);
    try {
      await api.post("/admin/vouchers", {
        ...form,
        amount_chf: Number(form.amount_chf || 0),
      });
      toast.success("Gutschein erstellt");
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Erstellen fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink/60 flex items-start sm:items-center justify-center p-4 overflow-y-auto" data-testid="voucher-create-modal">
      <form onSubmit={submit} className="bg-white rounded-md max-w-lg w-full my-8 shadow-xl">
        <div className="p-5 sm:p-6 border-b border-border flex items-center justify-between">
          <h2 className="font-display text-2xl text-ink flex items-center gap-2"><Gift className="w-5 h-5 text-terracotta" strokeWidth={1.5}/> Gutschein manuell erstellen</h2>
          <button type="button" onClick={onClose} className="text-ink-muted hover:text-ink text-2xl leading-none">×</button>
        </div>
        <div className="p-5 sm:p-6 space-y-4">

          {/* Typ-Umschalter */}
          <div>
            <span className="text-xs uppercase tracking-widest text-ink-muted">Was für ein Gutschein?</span>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setForm({ ...form, voucher_type: "amount", service_id: "" })}
                className={`px-3 py-2.5 rounded-md border text-sm ${form.voucher_type === "amount" ? "border-terracotta bg-terracotta/10 text-terracotta" : "border-border bg-white text-ink-muted"}`}
                data-testid="cv-type-amount">
                💰 Geldbetrag (CHF)
              </button>
              <button type="button" onClick={() => setForm({ ...form, voucher_type: "service" })}
                className={`px-3 py-2.5 rounded-md border text-sm ${form.voucher_type === "service" ? "border-terracotta bg-terracotta/10 text-terracotta" : "border-border bg-white text-ink-muted"}`}
                data-testid="cv-type-service">
                🌿 Bestimmte Behandlung
              </button>
            </div>
          </div>

          {/* Betrag ODER Behandlung */}
          {form.voucher_type === "amount" ? (
            <div className="grid grid-cols-2 gap-4">
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-ink-muted">Betrag CHF *</span>
                <input type="number" min={1} required value={form.amount_chf} onChange={set("amount_chf")} className="mt-2 w-full px-3 py-2.5 border border-border rounded-md text-lg" data-testid="cv-amount" />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-ink-muted">Versandart *</span>
                <select value={form.delivery_method} onChange={set("delivery_method")} className="mt-2 w-full px-3 py-2.5 border border-border rounded-md" data-testid="cv-delivery">
                  <option value="pickup">🏠 Abholung in Praxis</option>
                  <option value="shipping">📮 Versand per Post</option>
                  <option value="email">✉️ Versand per E-Mail (PDF)</option>
                </select>
              </label>
            </div>
          ) : (
            <>
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-ink-muted">Behandlung wählen *</span>
                <select value={form.service_id} onChange={set("service_id")} required className="mt-2 w-full px-3 py-2.5 border border-border rounded-md" data-testid="cv-service">
                  <option value="">— Bitte wählen —</option>
                  {services.map((s) => (
                    <option key={s.id} value={s.id}>
                      {(s.name_de || s.slug)} · {s.duration_minutes || "?"} Min · CHF {s.price_chf}
                    </option>
                  ))}
                </select>
                {selectedService && (
                  <span className="text-[11px] text-ink-muted mt-1 block">
                    Wert des Gutscheins: <strong>CHF {selectedService.price_chf}</strong> (kann bei Einlösung auch angerechnet werden, falls andere Behandlung gewählt wird)
                  </span>
                )}
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-ink-muted">Versandart *</span>
                <select value={form.delivery_method} onChange={set("delivery_method")} className="mt-2 w-full px-3 py-2.5 border border-border rounded-md" data-testid="cv-delivery">
                  <option value="pickup">🏠 Abholung in Praxis</option>
                  <option value="shipping">📮 Versand per Post</option>
                  <option value="email">✉️ Versand per E-Mail (PDF)</option>
                </select>
              </label>
            </>
          )}

          <div className="pt-3 border-t border-border">
            <p className="text-xs uppercase tracking-widest text-ink-muted mb-2">Käuferin</p>
            <div className="space-y-3">
              <input type="text" placeholder="Name" value={form.buyer_name} onChange={set("buyer_name")} className="w-full px-3 py-2.5 border border-border rounded-md text-sm" data-testid="cv-buyer-name" />
              <div className="grid grid-cols-2 gap-3">
                <input type="email" placeholder={form.delivery_method === "email" ? "E-Mail (Pflicht für Versand!)" : "E-Mail (optional)"} required={form.delivery_method === "email"} value={form.buyer_email} onChange={set("buyer_email")} className="px-3 py-2.5 border border-border rounded-md text-sm" data-testid="cv-buyer-email" />
                <input type="tel" placeholder="Telefon (optional)" value={form.buyer_phone} onChange={set("buyer_phone")} className="px-3 py-2.5 border border-border rounded-md text-sm" data-testid="cv-buyer-phone" />
              </div>
            </div>
            {form.delivery_method === "email" && !form.buyer_email && (
              <p className="text-[11px] text-terracotta mt-2">⚠️ Für E-Mail-Versand ist eine E-Mail-Adresse nötig</p>
            )}
          </div>

          <div className="pt-3 border-t border-border">
            <p className="text-xs uppercase tracking-widest text-ink-muted mb-2">Für wen? (optional)</p>
            <input type="text" placeholder="Empfängerin (falls Geschenk)" value={form.recipient_name} onChange={set("recipient_name")} className="w-full px-3 py-2.5 border border-border rounded-md text-sm mb-3" data-testid="cv-recipient" />
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-ink-muted">Persönliche Nachricht auf dem Gutschein</span>
              <textarea rows={3} placeholder="Alles Gute zum Geburtstag …" value={form.message} onChange={set("message")} className="mt-2 w-full px-3 py-2.5 border border-border rounded-md text-sm resize-y" data-testid="cv-message" />
              <span className="text-[11px] text-ink-muted">Wird auf die PDF gedruckt (max ~3 Zeilen).</span>
            </label>
          </div>

          <div className="pt-3 border-t border-border">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-ink-muted">Status</span>
              <select value={form.initial_status} onChange={set("initial_status")} className="mt-2 w-full px-3 py-2.5 border border-border rounded-md" data-testid="cv-status">
                <option value="pending">Offen (noch nicht bezahlt)</option>
                <option value="paid">Bezahlt</option>
                <option value="picked_up">Bezahlt & abgeholt</option>
                <option value="sent">Versendet</option>
              </select>
            </label>
          </div>

          {/* Zusammenfassung */}
          <div className="pt-3 border-t border-border bg-sand-soft/50 -mx-5 sm:-mx-6 px-5 sm:px-6 py-3">
            <p className="text-[11px] uppercase tracking-widest text-ink-muted">Wert des Gutscheins</p>
            <p className="font-display text-2xl text-terracotta">CHF {effectiveAmount || 0}</p>
          </div>
        </div>
        <div className="p-5 sm:p-6 bg-muted/40 border-t border-border flex items-center justify-end gap-3 rounded-b-md">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-ink-muted hover:text-ink">Abbrechen</button>
          <button type="submit" disabled={saving} className="px-6 py-2.5 bg-terracotta text-white rounded-full text-sm hover:bg-terracotta-hover disabled:opacity-50" data-testid="cv-submit">
            {saving ? "Speichert…" : "Gutschein erstellen"}
          </button>
        </div>
      </form>
    </div>
  );
}
